import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CardQuickAccessTypeEnum } from '@vueling-ng/vy-ds/src/lib/components/composed/card-quick-access';
import { ModalConfig } from '@vueling-ng/vy-ds/src/lib/components/globals/modal';
import { catchError, Observable, Subject, SubscriptionLike, switchMap, take, throwError } from 'rxjs';
import { PaxServicesAndContactInformationComponent } from './components/pax-services-and-contact-information/pax-services-and-contact-information.component';

import { Router } from '@angular/router';
import { siteRoutes } from '@views/common/models/routes.model';
import { CardFeaturedModel } from '@vueling-ng/vy-ds/src/lib/components/composed/card-featured';
import { HelpersDataTransfer } from 'app/services/api-services/accountManager/models/transfer-session-model';
import {
  AvailableWebsites,
  AvailableWebsitesLegacy,
  redirectRoutes,
  redirectRoutes_Legacy
} from 'environments/common/redirect-routes';
import {
  BookingDetailsViewModel,
  configModalsConnectionError,
  ConfigModalsOutput,
  FeaturedCard
} from './models/booking-details.model';
import { BookingDetailsService } from './services/booking-details.service';

@Component({
  selector: 'app-booking-detail',
  templateUrl: './booking-detail.component.html',
  styleUrl: './booking-detail.component.scss'
})
export class BookingDetailComponent implements OnInit, OnDestroy {
  @ViewChild(PaxServicesAndContactInformationComponent)
  private readonly tripInfo!: PaxServicesAndContactInformationComponent;
  @ViewChild('tripInfoHeader')
  private readonly _tripInfoHeader!: ElementRef<HTMLDivElement>;
  private readonly _bookingDetailsService = inject(BookingDetailsService);
  private readonly _translateService = inject(TranslateService);
  private readonly _router = inject(Router);
  private readonly _location = inject(Location);

  private readonly sendMailWithoutPrice$: Subject<void> = new Subject<void>();
  private readonly sendMailWithPrice$: Subject<void> = new Subject<void>();
  private readonly goToHome$: Subject<void> = new Subject<void>();
  private _locationSubscription?: SubscriptionLike;

  public bookingDetailsViewModel$!: Observable<BookingDetailsViewModel>;
  public readonly cardsQuickAccessType = CardQuickAccessTypeEnum.SECONDARY;

  public get currentRedirectRoutes(): Record<AvailableWebsites, string> | Record<AvailableWebsitesLegacy, string> {
    return this._bookingDetailsService.getEnabledRedirectsMmb() ? redirectRoutes : redirectRoutes_Legacy;
  }
  public readonly alertConfig = this._bookingDetailsService.getCurrentAlert();
  private isOpenModal = false;

  public ngOnInit(): void {
    this.startConfigModals();
    this.loadBookingDetails();
    this.listenToNavigation();
  }

  public ngOnDestroy(): void {
    this._locationSubscription?.unsubscribe();
  }

  private listenToNavigation(): void {
    this._locationSubscription = this._location.subscribe(() => {
      if (this.isOpenModal) {
        this.isOpenModal = false;
        this._bookingDetailsService.closeModal();
      }
    });
  }

  public startConfigModals(): void {
    this.sendMailWithoutPrice$.subscribe(() => {
      this.downloadTripInfo(false);
    });
    this.sendMailWithPrice$.subscribe(() => {
      this.downloadTripInfo(true);
    });
    this.goToHome$.subscribe(() => {
      void this._router.navigate([siteRoutes.Home]);
    });
  }

  public handleQuickCardClick(url: string): void {
    url.endsWith('&d=') ? this.startTransferSession(url, true) : this.createdSharedCookies(url, true);
  }

  public loadBookingDetails(): void {
    this.bookingDetailsViewModel$ = this._bookingDetailsService.loadViewModel().pipe(
      catchError((err: HttpErrorResponse) => {
        const config = {
          ...configModalsConnectionError,
          primaryAction: { ...configModalsConnectionError.primaryAction!, onAction: this.goToHome$ }
        };
        this.isOpenModal = true;
        this._bookingDetailsService.openModal(config);
        return throwError(() => err);
      })
    );
  }

  public handlePendingProcessClick(): void {
    window.open(this.currentRedirectRoutes.ChangeItinerary);
  }

  public openModal(): void {
    this.tripInfo.openSections();
    const config: ModalConfig = {
      ...ConfigModalsOutput,
      primaryAction: { ...ConfigModalsOutput.primaryAction!, onAction: this.sendMailWithPrice$ },
      secondaryAction: { ...ConfigModalsOutput.secondaryAction!, onAction: this.sendMailWithoutPrice$ }
    };
    this.isOpenModal = true;
    this._bookingDetailsService.openModal(config);
  }

  public handleCardClick(item: CardFeaturedModel): void {
    const itemClicked = item as FeaturedCard;

    if (itemClicked.hasOutput) return this.openModal();

    itemClicked.postActionRedirectUrl!.endsWith('culture=')
      ? this.createdSharedCookies(itemClicked.postActionRedirectUrl!, itemClicked.targetBlank!)
      : this.startTransferSession(itemClicked.postActionRedirectUrl!, itemClicked.targetBlank!);
  }

  private downloadTripInfo(withPrice: boolean): void {
    this.bookingDetailsViewModel$
      .pipe(
        take(1),
        switchMap((vm) => {
          let tripSections = this.tripInfo.getSections(withPrice);
          tripSections = [this._tripInfoHeader, ...tripSections];
          return this._bookingDetailsService.downloadPDF(tripSections, vm.recordLocator);
        })
      )
      .subscribe();
  }

  private startTransferSession(baseUrl: string, targetBlank: boolean): void {
    this._bookingDetailsService
      .transferSessionB2C(this._bookingDetailsService.getCurrentState()())
      .pipe(catchError((err: HttpErrorResponse) => throwError(() => err)))
      .subscribe({
        next: ({ cryptData }) => {
          const culture = this._translateService.currentLang;
          const url = baseUrl.endsWith('&d=')
            ? `${baseUrl}${cryptData}`
            : `${baseUrl}?${'c'}${'='}${culture}&${HelpersDataTransfer.encryptams}${'='}${cryptData}`;

          this.redirectToExternalUrl(url, targetBlank);
        }
      });
  }

  private createdSharedCookies(url: string, targetBlank: boolean): void {
    this._bookingDetailsService.createCookie().subscribe({
      next: () => {
        const { email, recordLocator } = this._bookingDetailsService.getCurrentState()();
        const culture = this._translateService.currentLang;
        const base = url.endsWith('culture=') ? url.slice(0, -'culture='.length) : `${url}&`;
        const updateUrl = `${base}email=${encodeURIComponent(email)}&pnr=${recordLocator}&culture=${culture}`;
        this.redirectToExternalUrl(updateUrl, targetBlank);
      },
      error: (err: HttpErrorResponse) => {
        return throwError(() => err);
      }
    });
  }

  private redirectToExternalUrl(url: string, targetBlank: boolean): void {
    window.open(url, targetBlank ? '_blank' : '_self');
  }
}
